/* ----------------------------------------------------------------------------
 * Aura publish P1 — data layer for /v1/connector/publish-batches (owner decision
 * 10-10-2026). Workspace scope, session, CSRF and the idempotency header are all
 * server-enforced / added by lib/api. The server is AUTHORITATIVE for the
 * read-back: confirmBatch() proposes (server read-back + single-use token), then
 * executes with exactly that read-back echoed back. The token is held only in
 * memory for that one call and is never logged or stored.
 * -------------------------------------------------------------------------- */

import { api } from '@/lib/api';
import type { BatchChannel, BatchMode } from '@/voice/actions/batchPublishModel';

// lib/api buildUrl() prefixes API_BASE, which ALREADY ends in /v1 (absolute
// https://api.bookedcentral.com/v1 or the /v1 proxy). Paths here are RELATIVE to
// /v1: a leading /v1 doubled to /v1/v1/... and 404'd live on 10-10-2026 21:23 UTC
// (r2 owner device test, F1; the same bug shipped in rc1 19c837c). Guarded by
// data/__tests__/real-path/publish-batches-real-path.mjs (exact request URLs).
const BASE = '/connector/publish-batches';

export interface BatchCard {
  draft_id: number;
  title: string;
  excerpt: string;
  focus_keyword: string;
  target_page: string;
  state: 'not_on_site' | 'on_site_as_draft' | string;
}

export interface BatchSite { id: number; label: string; url: string; name: string }

export interface CandidatesResponse {
  ok: boolean;
  target: BatchSite;
  publish: { available: boolean; reason: string; message: string };
  items: BatchCard[];
  /** Re-push guard (owner decision 12-10-2026): drafts already LIVE on this target (never cards). */
  already_live?: { draft_id: number; title: string }[];
  reason: string;
}

export interface ProposeResponse {
  ok: boolean;
  batch_id: number;
  confirm_token: string;
  expires_at: string;
  target_id: number;
  site: BatchSite;
  count: number;
  draft_ids: number[];
  items: { draft_id: number; title: string }[];
  requested_mode: BatchMode;
  effective_mode: BatchMode;
  mode_reason: string;
  mode_message: string;
  spoken_readback: string;
}

export interface BatchItemResult {
  draft_id: number;
  status: 'published' | 'drafted' | 'skipped' | 'failed' | string;
  reason: string;
  publication_id: number | null;
  remote_post_id: string | null;
  remote_status: string | null;
  undo_available: boolean;
}

export interface ExecuteResponse {
  ok: boolean;
  batch_id: number;
  site: BatchSite;
  requested_mode: BatchMode;
  effective_mode: BatchMode;
  mode_reason: string;
  mode_message: string;
  counts: { published: number; drafted: number; skipped: number; failed: number };
  items: BatchItemResult[];
  spoken_result: string;
  replayed: boolean;
}

export function getBatchCandidates(targetId: number, limit: number, signal?: AbortSignal, draftIds?: number[]): Promise<CandidatesResponse> {
  // Aura P2 hand-off: draft_ids shows exactly the drafts just written (server keeps eligibility rules).
  const q: Record<string, string | number> = { target_id: targetId, limit };
  if (draftIds && draftIds.length > 0) q.draft_ids = draftIds.join(',');
  return api.get<CandidatesResponse>(`${BASE}/candidates`, q, signal);
}

export function proposeBatch(targetId: number, draftIds: number[], mode: BatchMode, channel: BatchChannel): Promise<ProposeResponse> {
  return api.post<ProposeResponse>(`${BASE}/propose`, { target_id: targetId, draft_ids: draftIds, mode, channel });
}

export function executeBatch(p: Pick<ProposeResponse, 'confirm_token' | 'target_id' | 'draft_ids' | 'requested_mode'>): Promise<ExecuteResponse> {
  return api.post<ExecuteResponse>(`${BASE}/execute`, {
    confirm_token: p.confirm_token,
    target_id: p.target_id,
    draft_ids: p.draft_ids,
    mode: p.requested_mode,
  });
}

/**
 * One confirmed action (a tap on Publish/Draft, "Publish all"/"Draft all", or a
 * spoken "publish"/"draft"): server read-back, then execute that exact read-back.
 * onReadback fires BETWEEN the two calls so voice can speak the server's
 * "Publishing 3 posts to booksesh.com" before anything is written.
 */
export async function confirmBatch(
  targetId: number,
  draftIds: number[],
  mode: BatchMode,
  channel: BatchChannel,
  onReadback?: (p: ProposeResponse) => void | Promise<void>,
): Promise<{ proposal: ProposeResponse; result: ExecuteResponse }> {
  const proposal = await proposeBatch(targetId, draftIds, mode, channel);
  if (onReadback) await onReadback(proposal);
  const result = await executeBatch(proposal);
  return { proposal, result };
}

export function revertPublication(publicationId: number): Promise<{ ok: boolean; changed: boolean; remote_status: string }> {
  return api.post(`${BASE}/revert`, { publication_id: publicationId });
}

// ---- Connector 1.3.0: update a LIVE post (owner GO 12-10-2026 15:53 UTC, option A) ----
// A SEPARATE verb set: propose returns the server read-back naming the live post
// ("Update the live post '<title>' on <site>?") + a single-use token; the UI confirms
// only on a pure yes or a tap, then executes; undo restores the plugin's saved copy.

export interface UpdateLiveProposal {
  ok: boolean;
  batch_id: number;
  confirm_token: string;
  expires_at: string;
  mode: 'update_live';
  target_id: number;
  draft_id: number;
  site: BatchSite;
  title: string;
  spoken_readback: string;
}

export interface UpdateLiveResult {
  ok: boolean;
  batch_id: number;
  mode: 'update_live';
  target_id: number;
  site: BatchSite;
  draft_id: number;
  title: string;
  status: 'updated' | 'failed' | string;
  reason: string;
  publication_id: number | null;
  snapshot_id: string | null;
  undo_available: boolean;
  spoken_result: string;
  replayed: boolean;
}

export function proposeUpdateLive(targetId: number, draftId: number, channel: BatchChannel): Promise<UpdateLiveProposal> {
  return api.post<UpdateLiveProposal>(`${BASE}/update-live/propose`, { target_id: targetId, draft_id: draftId, channel });
}

export function executeUpdateLive(p: Pick<UpdateLiveProposal, 'confirm_token' | 'target_id' | 'draft_id'>): Promise<UpdateLiveResult> {
  return api.post<UpdateLiveResult>(`${BASE}/update-live/execute`, {
    confirm_token: p.confirm_token,
    target_id: p.target_id,
    draft_id: p.draft_id,
  });
}

export function undoUpdateLive(batchId: number): Promise<{ ok: boolean; changed: boolean; spoken_result: string; restored_snapshot_id: string }> {
  return api.post(`${BASE}/update-live/undo`, { batch_id: batchId });
}
